import React, { JSX } from 'react';
import rehypeRaw from 'rehype-raw';
import ReactMarkdown from 'react-markdown';
import { isEmpty, cloneDeep } from 'lodash';
import { Container } from 'react-bootstrap';
import { Button } from '@progress/kendo-react-buttons';
import { useToggle } from 'usehooks-ts';

import {
  ContentAnswer,
  ScoringReductionEntity
} from '../../types/entities/task';
import {
  AchievementContent,
  AchievementContext
} from '../../contexts/achievement';
import { Gallery } from '../../components/gallery';
import {
  GetCustomFeedback,
  GetFormMedia,
  GetTaskContentById,
  isContainPremessageOrIntro
} from '../../utils/game-document/assets/task-contents';
import { Answer } from '../../components/questionnaire';
import {
  CalculateTotal,
  GetTaskById as GetPlayerTaskById,
  UpdateTaskAsync
} from '../../utils/game-engine/assets/tasks';
import {
  CheckboxOption,
  TaskContentAnswer,
  TaskContentForm
} from '../../types/game-document/entities/task-content';
import { TaskContentAnswerEntity } from '../../types/entities';
import { GameDocumentContext } from '../../contexts/game-document';
import { UpdatePlayerStateAsync } from '../../utils/game-engine/base';
import { GetResourceValue } from '../../utils/game-document/resources';
import {
  GiveScore,
  setPlayerScoreReduction
} from '../../utils/game-engine/player';
import { DisplayLanguageContext } from '../../contexts/display-languages';
import { PlayerContext, PlayerContextState } from '../../contexts/player';
import { defaultFeedbackMessage, defaultImage } from './task-content';
import { generateTitleById } from '../../utils/game-document/display-languages';
import { ConfirmationDialog } from '../../components/dialog/confirmation-dialog';
import { PlayerStateFull } from '../../types/state';
import {
  NotificationContent,
  NotificationContext
} from '../../contexts/notification';
import { addManualScoringAsync } from '../../services/players';
import RichtextContent from '../../components/richtext-content';
import { detectDeviceType } from '../../utils/device-type';
import { ArMiniGames, getOtherBaseUrl } from '../../constants/argame';
import { TFileUpload, useUploadChunk } from '../../hooks/use-upload-chunk';
import { getFromStore, putToStore } from '../../utils/indexedDB';
import { GameContext } from '../../contexts/game';
import { TScoring } from '../../hooks/use-scoring-on-background';
import { Resource } from '../../types/game-document';
import { processMediaTags } from '../../utils/game-engine/media-tag-converter';
import { TimerProgressForm } from '../../components/timer/progress-form';

export const OnDemandTask: React.FC<{ taskContentId: string }> = ({
  taskContentId
}) => {
  const [state] = React.useContext(GameDocumentContext);
  const [, , , isOnline] = React.useContext(GameContext);
  const [player, setPlayer] = React.useContext(PlayerContext);
  const [toast, setToast] = React.useContext(AchievementContext);
  const [displayLanguageContext] = React.useContext(DisplayLanguageContext);
  const [notification, setNotification] = React.useContext(NotificationContext);

  const [currentFormIndex, setCurrentFormIndex] = React.useState(0);
  const [hasIntro, setHasIntro] = React.useState<{
    content: string;
    embeddedBlobs: Resource['embeddedBlobs'];
  }>();
  const [checkboxOption, setCheckboxOption] = React.useState<CheckboxOption[]>(
    []
  );
  const [contentAnswer, setContentAnswer] = React.useState<ContentAnswer[]>([]);
  const [options, setOptions] = React.useState<string[]>([]);
  const [totalTryAnswer, setTotalTryAnswer] = React.useState<number>(0);
  const [questionScoringReduction, setQuestionScoringReduction] =
    React.useState<ScoringReductionEntity>();
  const [selectedTab, setSelectedTab] =
    React.useState<string>('Task information');
  const [showARWarning, setShowARWarning] = useToggle();

  const {
    isUploading,
    uploadStatus,
    uploadErrors,
    handleFileChange,
    retryUpload,
    cancelUpload
  } = useUploadChunk({
    onSuccessCallback: (res) => {
      updateAnswerValue(res.url, res.contextSize);
    }
  });
  const deviceType = detectDeviceType();

  const selectedTaskContent = GetTaskContentById(
    state.gameDocument,
    taskContentId
  );

  const [showNoAsnwerWarning, setShowNoAsnwerWarning] =
    React.useState<boolean>(false);

  const formQuestions = selectedTaskContent?.forms ?? [];
  const currentFormId = formQuestions.length
    ? formQuestions[currentFormIndex].id
    : '';
  const currentAnswerType = formQuestions.length
    ? formQuestions[currentFormIndex].type
    : 'none';

  const formMedia = React.useMemo(() => {
    if (state.gameDocument && formQuestions.length > 0)
      return GetFormMedia(state.gameDocument, formQuestions[currentFormIndex]);

    return [];
  }, [state.gameDocument, formQuestions, currentFormIndex]);

  const handleCloseODT = () => {
    setPlayer((prev) =>
      UpdatePlayerStateAsync(prev, {
        ...prev.playerState!,
        openedOnDemandTask: ''
      })
    );
  };

  const handleNavigation = (navigation: 'prev' | 'next') =>
    setCurrentFormIndex((prev) =>
      navigation === 'prev' ? (prev -= 1) : (prev += 1)
    );

  const generatePopupMessage = (
    answerTypeProps?: typeof currentAnswerType
  ): string => {
    switch (answerTypeProps) {
      case 'image':
        return (
          generateTitleById(
            '42b77164-cdf8-4222-94ad-4e5652b6b2f8',
            state,
            displayLanguageContext.displayLanguageSelected.resources!
          ) || 'No photo submitted. Please try again.'
        );
      case 'video':
        return (
          generateTitleById(
            '4981eed4-5e4a-4f90-819e-385c7634a48e',
            state,
            displayLanguageContext.displayLanguageSelected.resources!
          ) || 'No video submitted. Please try again.'
        );
      case 'file':
        return 'No file submitted. Please try again.';
      default:
        return 'No answer submitted. Please try again.';
    }
  };

  const setAnswer = (formId: string) => {
    setContentAnswer([]);
    const task = GetPlayerTaskById(player, taskContentId);
    if (task) {
      const answer = task?.taskContentFormAnswers?.find(
        (x) => x?.formId === formId
      );

      if (answer && answer.answers) {
        setContentAnswer(cloneDeep(answer.answers));
      }
    }
  };

  const setSelectedCheckboxOptions = (answers: TaskContentAnswer[]) => {
    if (currentAnswerType === 'checkbox') {
      if (currentFormIndex > -1) {
        let option: CheckboxOption[] = [];
        if (answers) {
          answers?.forEach((answer) => {
            const description = GetResourceValue(
              state.gameDocument!,
              answer.answerResId,
              player?.playerState?.language?.name!
            );
            option.push({
              answerResId: answer.answerResId,
              description: description ?? '',
              isChecked:
                (contentAnswer?.find(
                  (x) => x.answerResId === answer?.answerResId
                )?.value as boolean) ?? false
            });
          });

          setCheckboxOption(cloneDeep(option));
        }
      }
    }
  };

  const getOptions = (options: TaskContentAnswer[]) => {
    let optionsDescription: string[] = [];

    options?.forEach((opt) => {
      const description = GetResourceValue(
        state.gameDocument!,
        opt.answerResId,
        player?.playerState?.language?.name!
      );
      if (description !== undefined) {
        optionsDescription.push(description);
      }
    });

    setOptions(cloneDeep(optionsDescription));
  };

  const repopulateQuestion = () => {
    const forms = cloneDeep(formQuestions);
    if (forms && forms[currentFormIndex]) {
      const form = cloneDeep(forms[currentFormIndex]);
      getOptions(form.answers ?? []);
      setSelectedCheckboxOptions(cloneDeep(form.answers ?? []));
      setAnswer(form.id);
    }
  };

  const getScoringReduction = async () => {
    if (player.playerState && formQuestions) {
      const formId = formQuestions[currentFormIndex]?.id;
      let task = GetPlayerTaskById(player, taskContentId);
      if (task) {
        const newPlayerState = await setPlayerScoreReduction(
          player,
          taskContentId,
          formQuestions,
          currentFormIndex,
          task,
          formId
        );
        if (newPlayerState) {
          setPlayer((prev) => UpdatePlayerStateAsync(prev, newPlayerState));
          let isExistAnswer = task?.taskContentFormAnswers?.find(
            (x: TaskContentAnswerEntity) => x?.formId === formId
          );

          if (isExistAnswer) {
            const currentTaskContentFormAnswers =
              task?.taskContentFormAnswers![currentFormIndex];
            setQuestionScoringReduction(
              currentTaskContentFormAnswers.scoringReductions
            );
          }
        }
      }
    }
  };

  const getPlayerAnswerStatus = (): boolean | undefined => {
    if (currentFormIndex < 0) return;
    if (player.playerState && formQuestions && taskContentId) {
      return (
        GetPlayerTaskById(player, taskContentId)?.taskContentFormAnswers?.find(
          (x) => x?.formId === formQuestions[currentFormIndex]?.id
        )?.isCorrectAnswer ?? undefined
      );
    }
    return undefined;
  };

  const getPlayerAttemptsLeft = (): number => {
    if (currentFormIndex < 0) return -1;
    if (player.playerState && formQuestions && taskContentId) {
      return (
        GetPlayerTaskById(player, taskContentId)?.taskContentFormAnswers?.find(
          (x) => x?.formId === formQuestions[currentFormIndex]?.id
        )?.attemptsLeft ?? -1
      );
    }
    return -1;
  };

  const updateAnswerValue = (
    value: string | number | undefined,
    size?: number
  ) => {
    if (contentAnswer.length === 0) {
      let newAnswer: ContentAnswer[] = [];
      const form = cloneDeep(formQuestions[currentFormIndex]);
      if (
        form &&
        !['video', 'image', 'file'].includes(currentAnswerType ?? '')
      ) {
        form?.answers?.forEach((answer) => {
          newAnswer.push({
            answerResId: answer?.answerResId,
            value: value,
            size: size,
            score: answer?.score
          });
        });
      }

      if (['video', 'image', 'file'].includes(currentAnswerType ?? '')) {
        newAnswer.push({
          value,
          size,
          answerResId: ''
        });
      }

      setContentAnswer(cloneDeep(newAnswer));
    } else {
      let newAnswer: ContentAnswer[] = cloneDeep(contentAnswer);
      newAnswer[0].value = value;
      newAnswer[0].size = size;
      setContentAnswer(cloneDeep(newAnswer));
    }
  };

  //On input change (single input like Text, Textarea, Date, Number)
  const onSingleInputChangeAnswer = (
    value: string | number | File,
    size?: number
  ) => {
    if (typeof value === 'string' || typeof value === 'number') {
      updateAnswerValue(value as string | number, size);
    } else {
      const taskId = taskContentId;
      const formId = currentFormId;
      const isManualScorring =
        formQuestions[currentFormIndex].isManualScoring ?? false;

      updateAnswerValue('');
      handleFileChange(value, taskId, formId, isManualScorring)
        .then((res) => {
          if (res) updateAnswerValue(res.url, res.contextSize);
        })
        .catch((err) => {
          console.error('Failed to handle file change in ODT:', err);
        });
    }
  };

  //On radio button index change
  const onRadioChangeAnswer = (value: number) => {
    let newAnswer: ContentAnswer[] = [];
    const form = cloneDeep(formQuestions[currentFormIndex]);
    if (form) {
      form?.answers?.forEach((answer, index) => {
        newAnswer.push({
          answerResId: answer?.answerResId,
          value: (index === value) as boolean,
          score: answer?.score
        });
      });
      setContentAnswer(cloneDeep(newAnswer));
    }
  };

  const onCheckboxChangeHandler = React.useCallback(
    (index: number, value: boolean) => {
      const form = cloneDeep(formQuestions[currentFormIndex]);
      if (form) {
        const answerResId = form.answers
          ? form.answers[index]?.answerResId
          : '';
        let newContentAnswer: ContentAnswer[] = cloneDeep(contentAnswer);

        if (answerResId !== '') {
          const answerIndex = newContentAnswer?.findIndex(
            (x) => x.answerResId === answerResId
          );

          if (answerIndex > -1) {
            newContentAnswer[answerIndex].value = value;
            newContentAnswer[answerIndex].score = form.answers
              ? form.answers[answerIndex]?.score
              : 0;
          }

          if (answerIndex === -1) {
            const score = form.answers ? form.answers[answerIndex]?.score : 0;

            newContentAnswer.push({
              answerResId: answerResId,
              value: value,
              score
            });
          }

          if (value) {
            setTotalTryAnswer(totalTryAnswer + 1);
          } else {
            setTotalTryAnswer(totalTryAnswer - 1);
            //if player uncheck answer, remove answer from content answer
            newContentAnswer.splice(answerIndex, 1);
          }

          setContentAnswer(cloneDeep(newContentAnswer));

          const newCheckboxItem = checkboxOption.at(index);
          if (newCheckboxItem) {
            newCheckboxItem.isChecked = value;
            setCheckboxOption((prev) => {
              prev.splice(index, 1, newCheckboxItem);
              return prev;
            });
          }
        }
      }
    },
    [formQuestions, currentFormIndex, contentAnswer]
  );

  const isDisabledAnswer = () => {
    const isTimeRemaining = formQuestions[currentFormIndex].isScoringReduction
      ? (questionScoringReduction?.maximumTimeLimit ?? 0) < 1
      : false;

    return (
      getPlayerAttemptsLeft() === 0 ||
      getPlayerAnswerStatus() === true ||
      (getPlayerAttemptsLeft() === -1 && getPlayerAnswerStatus() === false) ||
      isTimeRemaining
    );
  };

  const calculateAnswerWithScore = (playerAnswers: ContentAnswer[]) => {
    const playerContentAnswers = formQuestions[
      currentFormIndex
    ].answers?.filter((answer) =>
      playerAnswers.some((i) => i.answerResId === answer.answerResId)
    );

    const calculateScore = playerContentAnswers?.reduce((total, item) => {
      const score =
        item?.score && typeof item.score === 'number' ? item.score : 0;
      return total + score;
    }, 0);
    return calculateScore ?? 0;
  };

  const getQuestionCorrectAnswers = () => {
    if (validateScoring()) {
      return (
        formQuestions[currentFormIndex].answers?.filter((x) => x.isChecked) ??
        []
      );
    }
    return [];
  };

  const getCheckboxScore = (form: TaskContentForm) => {
    if (form) {
      let playerAnswers = contentAnswer.filter((x) => x.value as boolean);
      let score = calculateAnswerWithScore(playerAnswers);

      return score;
    }
    return 0;
  };

  const checkboxAnswerCheckHandler = (form: TaskContentForm) => {
    if (form) {
      const playerAnswers = contentAnswer.filter((x) => x.value as boolean);

      const correctOptions = getQuestionCorrectAnswers().map(
        (x) => x.answerResId
      );

      const wrongOptions = form.answers
        ?.filter((i) => !i.isChecked)
        ?.map((i) => i.answerResId);

      const checkedCorrectOptions = playerAnswers.filter((x) =>
        correctOptions.includes(x.answerResId)
      ).length;

      const checkedWrongOptions = wrongOptions
        ? playerAnswers.filter((x) => wrongOptions.includes(x.answerResId))
            .length
        : 0;

      return (
        (checkedWrongOptions === 0 || !checkedWrongOptions) &&
        checkedCorrectOptions !== 0
      );
    }
    return false;
  };

  const getAnswerScore = (form: TaskContentForm) => {
    if (form) {
      if (form.type === 'checkbox') {
        return getCheckboxScore(form);
      } else if (form.type === 'radio') {
        let playerAnswers = contentAnswer.filter((x) => x.value as boolean);
        return calculateAnswerWithScore(playerAnswers);
      }

      return form.score ?? 0;
    }
  };

  const updateAnswerToPlayerState = async () => {
    if (player.playerState && formQuestions) {
      let task = GetPlayerTaskById(player, taskContentId);

      if (task) {
        let isExistAnswer = task?.taskContentFormAnswers?.find(
          (x) => x?.formId === currentFormId
        );
        const isScoringReduction =
          formQuestions[currentFormIndex].isScoringReduction;

        if (isExistAnswer && !isExistAnswer.isScoringReduction) {
          task.taskContentFormAnswers![currentFormIndex].answers =
            cloneDeep(contentAnswer);
        } else {
          if (isScoringReduction) {
            task.taskContentFormAnswers![currentFormIndex].answers =
              cloneDeep(contentAnswer);
            task.taskContentFormAnswers![currentFormIndex].isCorrectAnswer =
              true;
            task.taskContentFormAnswers![currentFormIndex].attemptsLeft = task
              .taskContentFormAnswers![currentFormIndex].attemptsLeft
              ? task.taskContentFormAnswers![currentFormIndex].attemptsLeft
              : formQuestions[currentFormIndex].maxAttempts
                ? formQuestions[currentFormIndex].maxAttempts
                : -1;
            task.taskContentFormAnswers![currentFormIndex].score =
              getAnswerScore(formQuestions[currentFormIndex]);
          } else {
            if (!isExistAnswer) {
              let newForm: TaskContentAnswerEntity = {
                formId: currentFormId,
                answers: cloneDeep(contentAnswer),
                attemptsLeft:
                  formQuestions[currentFormIndex]?.maxAttempts ?? -1,
                isCorrectAnswer: true,
                score: getAnswerScore(formQuestions[currentFormIndex])
              };

              if (!task.taskContentFormAnswers) {
                task.taskContentFormAnswers = [];
              }
              task.taskContentFormAnswers?.push(newForm);
            }
          }
        }

        return UpdateTaskAsync(player, taskContentId, task);
      }
    }
  };

  const validateScoring = () => {
    const playerAttemptsLeft = getPlayerAttemptsLeft();

    // Task has manual scoring, no need to check for acceptable answers
    const isManualScorring = formQuestions[currentFormIndex].isManualScoring;
    return (
      formQuestions &&
      currentFormIndex > -1 &&
      playerAttemptsLeft !== 0 &&
      !isManualScorring
    );
  };

  const checkCorrectAnswer = async (): Promise<boolean> => {
    let isAnswerCorrect = true;

    if (validateScoring()) {
      if (currentAnswerType === 'text') {
        //Check default answer has been set
        let acceptableAnswersData: string = '';
        const taskAnswer = formQuestions[currentFormIndex];
        if (taskAnswer && taskAnswer.answers) {
          acceptableAnswersData = GetResourceValue(
            state.gameDocument!,
            taskAnswer.answers[0].answerResId,
            player?.playerState?.language?.name ?? ''
          );
        }

        //compare player answer with acceptable answer
        if (!isEmpty(acceptableAnswersData)) {
          // check is acceptable answer is using wildcard or not
          const isWildcard = (answer: string) =>
            new RegExp(`^\\.\\*.*${answer}.*\\.\\*$`).test(answer);

          const answer = contentAnswer[0]?.value;
          // split multiple answer using |
          const patternMap = acceptableAnswersData
            .split('|')
            .map((acceptableAnswer) => {
              // if using wildcard, return regex instance
              return isWildcard(acceptableAnswer)
                ? new RegExp(
                    acceptableAnswer
                      ? acceptableAnswer.toLowerCase()
                      : acceptableAnswer
                  )
                : acceptableAnswer;
            });

          // Try to match the answer with acceptable answer using
          isAnswerCorrect = patternMap.some((pattern) =>
            pattern instanceof RegExp
              ? pattern.test(answer ? answer.toLowerCase() : answer)
              : pattern === answer
          );
        } else {
          //Task has no default answer, set it correct by default
          isAnswerCorrect = true;
        }
      } else if (
        currentAnswerType === 'textarea' ||
        currentAnswerType === 'date' ||
        currentAnswerType === 'time'
      ) {
        //Check default answer has been set
        let acceptableAnswers: string = '';
        const taskAnswer = formQuestions[currentFormIndex];
        if (taskAnswer && taskAnswer.answers) {
          acceptableAnswers = GetResourceValue(
            state.gameDocument!,
            taskAnswer.answers[0].answerResId,
            player?.playerState?.language?.name ?? ''
          );
        }

        //compare player answer with acceptable answer
        if (!isEmpty(acceptableAnswers)) {
          isAnswerCorrect =
            contentAnswer.length > 0 &&
            contentAnswer[0]?.value?.toLowerCase() ===
              acceptableAnswers?.toLowerCase();
        } else {
          //Task has no default answer, set it correct by default
          isAnswerCorrect = true;
        }
      } else if (currentAnswerType === 'number') {
        // Get the form
        const form = formQuestions[currentFormIndex];

        if (form && form.answers && form.answers.length >= 2) {
          // Check the answer range
          const minRange = GetResourceValue(
            state.gameDocument!,
            form.answers[0].answerResId,
            player?.playerState?.language?.name ?? ''
          );
          const maxRange = GetResourceValue(
            state.gameDocument!,
            form.answers[1].answerResId,
            player?.playerState?.language?.name ?? ''
          );
          const requiresRange = minRange !== '' && maxRange !== '';

          if (requiresRange && form.answers && form.answers.length >= 2) {
            // Check if minRange and maxRange are equal
            if (minRange === maxRange) {
              const playerAnswer = parseInt(
                contentAnswer[0]?.value?.toString()
              );
              isAnswerCorrect = playerAnswer === parseInt(minRange);
            }

            // Validate answer within range
            if (
              minRange !== '' &&
              maxRange !== '' &&
              contentAnswer.length > 0
            ) {
              const playerAnswer = parseInt(
                contentAnswer[0]?.value?.toString()
              );
              isAnswerCorrect =
                playerAnswer >= parseInt(minRange) &&
                playerAnswer <= parseInt(maxRange);
            }
          }

          if (!requiresRange) {
            // Validate it should be greater than minRange
            if (minRange) {
              isAnswerCorrect =
                parseInt(contentAnswer[0]?.value?.toString()) >=
                parseInt(minRange);
            }

            // Validate it should be less than maxRange
            if (maxRange) {
              isAnswerCorrect =
                parseInt(contentAnswer[0]?.value?.toString()) <=
                parseInt(maxRange);
            }
          }
        } else {
          //Task has no default answer, set it correct by default
          isAnswerCorrect = true;
        }
      } else if (currentAnswerType === 'checkbox') {
        if (validateScoring()) {
          isAnswerCorrect = checkboxAnswerCheckHandler(formQuestions[0]!);
        }
      } else if (currentAnswerType === 'radio') {
        const correctAnswer = formQuestions[currentFormIndex].answers?.find(
          (x) => x.isChecked
        );
        const playerAnswer = contentAnswer.find((x) => x.value as boolean);
        isAnswerCorrect =
          contentAnswer.length > 0 &&
          correctAnswer?.answerResId === playerAnswer?.answerResId;
      } else if (
        currentAnswerType === 'image' ||
        currentAnswerType === 'video'
      ) {
        isAnswerCorrect = contentAnswer.length > 0;
      }
    }

    const task = GetPlayerTaskById(player, taskContentId);

    if (task) {
      const form = cloneDeep(
        task.taskContentFormAnswers?.find(
          (x) => x?.formId === formQuestions[currentFormIndex].id
        )
      );
      if (!isAnswerCorrect) {
        if (form && form.score !== undefined) {
          form.score =
            form.score -
            (formQuestions[currentFormIndex]?.pointDepreciation ?? 0);
        }
        // #6529 If attempsLeft is finished and the answer is still wrong, the score will immediately be changed to 0
        if (form && form.attemptsLeft && form.attemptsLeft - 1 <= 0) {
          form.score = 0;
        }
        if (form && form.attemptsLeft && form.attemptsLeft > 0) {
          form.attemptsLeft = form.attemptsLeft - 1;
        }
      }

      if (form && form.isCorrectAnswer !== undefined) {
        form.isCorrectAnswer = isAnswerCorrect;
      }

      task.taskContentFormAnswers![currentFormIndex] = form!;

      setPlayer((prev) => {
        const response = UpdateTaskAsync(prev, taskContentId, task);
        return UpdatePlayerStateAsync(prev, response);
      });
    }

    return isAnswerCorrect;
  };

  const getPlayerScore = (): number => {
    if (currentFormIndex < 0) return -1;
    if (player.playerState && formQuestions && taskContentId) {
      if (currentAnswerType !== 'checkbox' && currentAnswerType !== 'radio') {
        return (
          GetPlayerTaskById(
            player,
            taskContentId
          )?.taskContentFormAnswers?.find(
            (x) => x?.formId === formQuestions[currentFormIndex]?.id
          )?.score ?? 0
        );
      } else {
        // #6695
        //new behaviour calculate player score when answer type is checkbox or radio
        let playerAnswers = contentAnswer.filter((x) => x.value as boolean);

        const score =
          currentAnswerType === 'radio'
            ? calculateAnswerWithScore(playerAnswers)
            : getCheckboxScore(formQuestions[currentFormIndex]);

        return score;
      }
    }
    return 0;
  };

  const updateTaskStatusToCompleted = () => {
    setPlayer((prev) => {
      const task = GetPlayerTaskById(prev, taskContentId);
      if (task) {
        task.status = 'completed';
        const awardedScore =
          task.taskContentFormAnswers?.reduce(
            (total, item) => total + (item.score ?? 0),
            0
          ) ?? 0;

        let response: PlayerStateFull = UpdateTaskAsync(
          prev,
          taskContentId,
          task!
        );
        response.openedOnDemandTask = '';
        response = GiveScore(prev, awardedScore);

        return UpdatePlayerStateAsync(prev, response);
      }
      return prev;
    });
  };

  const renderPlayerAttempts = (): JSX.Element => {
    const attemptsLeft: number = getPlayerAttemptsLeft();
    if (formQuestions) {
      const maxAttempts: number =
        currentFormIndex > -1
          ? (formQuestions[currentFormIndex]?.maxAttempts ?? -1)
          : -1;

      if (attemptsLeft > 0 && attemptsLeft < maxAttempts) {
        return (
          <>
            {(
              generateTitleById(
                '298f372e-f97b-42c6-b1a7-a0bf7866ec7b',
                state,
                displayLanguageContext.displayLanguageSelected.resources!,
                'game'
              ) || 'Try again - % attempt(s) remaining'
            ).replace('%', attemptsLeft.toString())}
          </>
        );
      }
    }
    return <></>;
  };

  // hidden close, previous, and next button if
  // question with timer
  // timer still running
  // and not answered yet
  const isUnableToCloseOrChangeForm = React.useMemo(() => {
    if (hasIntro) return false;

    const isAnsweredQuestion = getPlayerAnswerStatus();
    if (
      currentFormIndex > -1 &&
      isAnsweredQuestion === undefined &&
      formQuestions &&
      questionScoringReduction?.maximumTimeLimit &&
      questionScoringReduction.maximumTimeLimit > 0
    ) {
      const currentQuestionForm = formQuestions[currentFormIndex];
      return currentQuestionForm.isScoringReduction;
    }

    return false;
  }, [hasIntro, currentFormIndex, formQuestions, questionScoringReduction]);

  const addManualScoring = async () => {
    const content: NotificationContent[] = notification.content;
    const form = formQuestions[currentFormIndex];
    const answerTask = player?.playerState?.tasks?.find(
      (x) => x.id === taskContentId
    );
    const answer = answerTask?.taskContentFormAnswers?.find(
      (x) => x?.formId === currentFormId
    );

    if (form) {
      // set message & iconType toast notification if question type manual scoring
      const manualScoringNotification: NotificationContent = {
        icon: 'notifications',
        isHide: false,
        message:
          generateTitleById(
            '32681cc9-9ee0-4d19-81fa-7a2b6e69a362',
            state,
            displayLanguageContext.displayLanguageSelected.resources!,
            'game'
          ) || 'Your answer has been submitted to the Facilitator for review'
      };
      content.push(manualScoringNotification);

      setNotification({
        ...notification,
        content
      });

      if (isOnline) {
        await addManualScoringAsync(
          state.gameCode,
          player?.playerState?.code,
          taskContentId,
          form?.id,
          JSON.stringify(answer)
        );
      } else if (
        !isOnline &&
        !['video', 'image', 'file'].includes(currentAnswerType)
      ) {
        const scoringQueue = await getFromStore<TScoring>(
          'scoring-queue',
          player.playerState?.code ?? ''
        );
        if (scoringQueue) {
          const selectedScoring = scoringQueue.scorings.find(
            (s) => s.taskId === taskContentId && s.formId === form.id
          );
          if (!selectedScoring) {
            const scorings = cloneDeep(scoringQueue.scorings);
            scorings.push({
              taskId: taskContentId,
              formId: form.id,
              answer: JSON.stringify(answer)
            });
            await putToStore('scoring-queue', {
              playerCode: player.playerState?.code ?? '',
              scorings
            });
          }
        }
      }
    }
  };

  const onSubmitHandler = async () => {
    if (!formQuestions) return;

    const newPlayerState = await updateAnswerToPlayerState();
    const isManualScorring = formQuestions[currentFormIndex].isManualScoring;
    const formId = currentFormId;

    if (newPlayerState) {
      setPlayer((prev) => UpdatePlayerStateAsync(prev, newPlayerState));
      const isCorrectAnswer: boolean = await checkCorrectAnswer();
      const newTask = GetPlayerTaskById(player, taskContentId);
      const newForm = newTask!.taskContentFormAnswers?.find(
        (x) => x?.formId === formId
      );
      const totalScoreReduction =
        newForm?.scoringReductions?.totalScoreReduction ?? 0;
      const totalOriginalScore = getPlayerScore();
      const totalAwaredScore = totalOriginalScore - totalScoreReduction;
      const awardedScore =
        totalAwaredScore < 1 &&
        currentAnswerType !== 'checkbox' &&
        currentAnswerType !== 'radio'
          ? 0
          : Math.round(totalAwaredScore);

      if (isManualScorring) addManualScoring();

      // #4505, update status to complete if player already answered all questions regardless correct or incorrect status
      const playerTasks = GetPlayerTaskById(player, taskContentId);
      const customFeedback = GetCustomFeedback(
        state.gameDocument,
        taskContentId,
        currentFormIndex
      );
      // #4867 Multiple attempt case :
      // 1. If wrong answer and has no attempt = set task as completed
      // 2. If correct answer and has attempt = set task as completed
      // 3. If wrong answer and has attempt = task not set as competed
      if (
        formQuestions?.length === playerTasks?.taskContentFormAnswers?.length &&
        playerTasks?.taskContentFormAnswers?.findIndex(
          (x) => x.isCorrectAnswer === undefined
        ) === -1 &&
        playerTasks.status !== 'completed' &&
        playerTasks &&
        (playerTasks.taskContentFormAnswers![currentFormIndex]
          .isCorrectAnswer ||
          (!playerTasks.taskContentFormAnswers![currentFormIndex]
            .isCorrectAnswer &&
            playerTasks.taskContentFormAnswers![currentFormIndex]
              .attemptsLeft! <= 0))
      ) {
        if (!isManualScorring) {
          if (currentAnswerType === 'checkbox') {
            getCheckboxMessage(
              totalOriginalScore,
              awardedScore,
              isCorrectAnswer
            );
          } else {
            if (
              currentAnswerType !== 'textarea' &&
              currentAnswerType !== 'none'
            ) {
              getFeedbackMessage(
                totalOriginalScore,
                awardedScore,
                isCorrectAnswer
              );
            }
          }
        }

        if (isManualScorring || !customFeedback) {
          updateTaskStatusToCompleted();
        }
      } else {
        if (currentAnswerType === 'checkbox') {
          if (
            getPlayerAttemptsLeft() <= 0 ||
            getCheckboxScore(formQuestions[currentFormIndex]) ===
              formQuestions[currentFormIndex]?.score! ||
            (playerTasks?.taskContentFormAnswers![currentFormIndex]
              .isScoringReduction &&
              getPlayerAttemptsLeft() <= 0)
          ) {
            if (!isManualScorring) {
              getCheckboxMessage(
                totalOriginalScore,
                awardedScore,
                isCorrectAnswer
              );
            }

            // go to next question will handled here if question doesnt have feedback
            // and manual scoring
            if (isManualScorring || !customFeedback) {
              setTimeout(() => handleNavigation('next'), 500);
            }
          }
        } else {
          if (isCorrectAnswer || getPlayerAttemptsLeft() === -1) {
            if (
              currentAnswerType !== 'textarea' &&
              currentAnswerType !== 'none'
            ) {
              if (!isManualScorring) {
                getFeedbackMessage(
                  totalOriginalScore,
                  awardedScore,
                  isCorrectAnswer
                );
              }
            }

            // go to next question will handled here if question doesnt have feedback
            // and manual scoring
            if (isManualScorring || !customFeedback) {
              setTimeout(() => handleNavigation('next'), 500);
            }
          }
        }
      }

      // await deleteFromStore('upload-queue', `${taskContentId}-${formId}`);
    }
  };

  const updatePlayerAnswerScore = (
    playerContext: PlayerContextState,
    awardedScore: number
  ) => {
    if (playerContext && formQuestions) {
      const formId = formQuestions[currentFormIndex]?.id;
      const task = GetPlayerTaskById(playerContext, taskContentId);
      let taskContentAnswer = task?.taskContentFormAnswers?.find(
        (i) => i.formId === formId
      );
      if (taskContentAnswer) {
        taskContentAnswer.score = awardedScore;
      }
      return UpdateTaskAsync(playerContext, taskContentId, task!);
    }
  };

  const defaultMessage = React.useCallback(
    (
      type:
        | 'defaultAllCorrectFeedbackMessageResId'
        | 'defaultAllWrongFeedbackMessageResId'
        | 'defaultPartialCorrectFeedbackMessageResId'
    ) => {
      if (
        state.gameDocument?.settings &&
        state.gameDocument?.settings.designer
      ) {
        const message = GetResourceValue(
          state.gameDocument,
          state.gameDocument.settings.designer[type],
          player.playerState?.language.name
        );

        const value =
          message == '' ||
          (type === 'defaultAllCorrectFeedbackMessageResId' &&
            message === 'All correct feedback message default') ||
          (type === 'defaultAllWrongFeedbackMessageResId' &&
            message === 'All wrong feedback message default') ||
          (type === 'defaultPartialCorrectFeedbackMessageResId' &&
            message === 'Well done, you are partially correct.')
            ? defaultFeedbackMessage[type]
            : message;
        return value;
      }

      return defaultFeedbackMessage[type];
    },
    [state.gameDocument?.settings]
  );

  const imageUrl = React.useCallback(
    (
      res:
        | 'defaultFeedbackAllCorrectIconResId'
        | 'defaultFeedbackPartialCorrectIconResId'
        | 'defaultFeedbackAllWrongIconResId'
    ) => {
      if (
        state.gameDocument?.settings &&
        state.gameDocument?.settings.designer
      ) {
        const feedbackIcon = GetResourceValue(
          state.gameDocument!,
          state.gameDocument?.settings.designer[res],
          ''
        );
        return feedbackIcon !== '' ? feedbackIcon : defaultImage[res];
      } else {
        return defaultImage[res];
      }
    },
    [state.gameDocument?.settings]
  );

  const getCheckboxMessage = (
    totalOriginalScore: number,
    awardedScore: number,
    isCorrectAnswer: boolean
  ) => {
    const newPlayerState = updatePlayerAnswerScore(player, awardedScore);
    if (newPlayerState) {
      // Update answer score selected to player state
      setPlayer((prev) => {
        return UpdatePlayerStateAsync(prev, newPlayerState);
      });
    }

    let customFeedback = formQuestions[currentFormIndex].customFeedback;

    if (customFeedback) {
      // temporary note: "isCorrectAnswer" is a trigger to display a popup of the correct answer.
      if (totalOriginalScore && isCorrectAnswer) {
        const playerAnswers = contentAnswer.filter((x) => x.value as boolean);

        const correctOptions = getQuestionCorrectAnswers().map(
          (x) => x.answerResId
        );

        const checkedCorrectOptions = playerAnswers.filter((x) =>
          correctOptions.includes(x.answerResId)
        ).length;

        const isPartialCorrect =
          correctOptions.length !== checkedCorrectOptions;

        const isDefault = isPartialCorrect
          ? customFeedback.isDefaultPartiallyCorrect
          : customFeedback.isDefaultAllCorrect;

        const url = isDefault
          ? imageUrl(
              isPartialCorrect
                ? 'defaultFeedbackPartialCorrectIconResId'
                : 'defaultFeedbackAllCorrectIconResId'
            )
          : GetResourceValue(
              state.gameDocument!,
              isPartialCorrect
                ? customFeedback?.partiallyCorrectUrlResId!
                : customFeedback?.allCorrectUrlResId!,
              ''
            );

        const message = isDefault
          ? defaultMessage(
              isPartialCorrect
                ? 'defaultPartialCorrectFeedbackMessageResId'
                : 'defaultAllCorrectFeedbackMessageResId'
            )
          : GetResourceValue(
              state.gameDocument!,
              isPartialCorrect
                ? customFeedback?.partiallyCorrectMessageResId!
                : customFeedback?.allCorrectMessageResId!,
              player?.playerState?.language?.name
            );

        showFeedbackToast(
          message,
          url,
          isPartialCorrect ? 'partial' : 'correct',
          awardedScore
        );
      } else {
        const isDefault = customFeedback.isDefaultAllWrong;

        const url = isDefault
          ? imageUrl('defaultFeedbackAllWrongIconResId')
          : GetResourceValue(
              state.gameDocument!,
              customFeedback?.allWrongUrlResId!,
              ''
            );

        const message = customFeedback.isDefaultAllWrong
          ? defaultMessage('defaultAllWrongFeedbackMessageResId')
          : GetResourceValue(
              state.gameDocument!,
              customFeedback?.allWrongMessageResId!,
              player?.playerState?.language?.name
            );

        showFeedbackToast(message, url, 'wrong');
      }
    }
  };

  const getFeedbackMessage = (
    totalOriginalScore: number,
    awardedScore: number,
    isCorrectAnswer: boolean
  ) => {
    let questionScore = formQuestions[currentFormIndex]!.score! ?? 0;
    if (currentAnswerType === 'radio') {
      // * Function to add up the scores of all correct answers
      const scoreCorrectAnswer = getQuestionCorrectAnswers().map(
        (x) => x.score ?? 0
      );
      questionScore = CalculateTotal(scoreCorrectAnswer);
    }

    const newPlayerState = updatePlayerAnswerScore(player, awardedScore);
    if (newPlayerState) {
      // Update answer score selected to player state
      setPlayer((prev) => {
        return UpdatePlayerStateAsync(prev, newPlayerState);
      });
    }

    const customFeedback = formQuestions[currentFormIndex].customFeedback;

    if (customFeedback) {
      // for answerType other than 'file', 'image' and 'video', then check the score
      // for answerType 'file', 'image' and 'video', then check isFileUploaded

      // temporary note: "isCorrectAnswer" is a trigger to display a popup of the correct answer.

      if (
        (Number.isFinite(totalOriginalScore) && isCorrectAnswer) ||
        currentAnswerType === 'video' ||
        currentAnswerType === 'image' ||
        currentAnswerType === 'file'
      ) {
        const isDefault = customFeedback.isDefaultAllCorrect;

        const url = isDefault
          ? imageUrl('defaultFeedbackAllCorrectIconResId')
          : GetResourceValue(
              state.gameDocument!,
              customFeedback?.allCorrectUrlResId!,
              ''
            );

        const message = isDefault
          ? defaultMessage('defaultAllCorrectFeedbackMessageResId')
          : GetResourceValue(
              state.gameDocument!,
              customFeedback?.allCorrectMessageResId!,
              player?.playerState?.language?.name
            );

        showFeedbackToast(message, url, 'correct', awardedScore);
      } else {
        const isDefault = customFeedback.isDefaultAllWrong;

        const url = isDefault
          ? imageUrl('defaultFeedbackAllWrongIconResId')
          : GetResourceValue(
              state.gameDocument!,
              customFeedback?.allWrongUrlResId!,
              ''
            );

        const message = isDefault
          ? defaultMessage('defaultAllWrongFeedbackMessageResId')
          : GetResourceValue(
              state.gameDocument!,
              customFeedback?.allWrongMessageResId!,
              player?.playerState?.language?.name
            );

        showFeedbackToast(message, url, 'wrong');
      }
    }
  };

  const showFeedbackToast = (
    message: string,
    imageUrl: string,
    status: 'correct' | 'wrong' | 'partial',
    score?: number
  ) => {
    const addItemNotification: AchievementContent = {
      isHide: false,
      imageUrl: imageUrl,
      priorityQueueNumber: 2,
      onCloseCallback: () => {
        const playerTasks = GetPlayerTaskById(player, taskContentId);
        if (formQuestions && playerTasks) {
          formQuestions.length === playerTasks.taskContentFormAnswers?.length
            ? updateTaskStatusToCompleted()
            : handleNavigation('next');
        }
      },
      message: (
        <>
          <div className={'d-flex justify-content-center flex-column h-100'}>
            <div
              className={
                'd-flex flex-column align-items-center justify-content-center mb-auto p-3'
              }>
              {score && (
                <span>{`${score} ${
                  score > 1
                    ? generateTitleById(
                        '4b8c7c2b-9d0f-5e60-ab2c-3d4e5f6a7b8c',
                        state,
                        displayLanguageContext.displayLanguageSelected
                          .resources!,
                        'game'
                      ) || 'Points'
                    : generateTitleById(
                        '3a7b6b1a-8c9e-4d5f-9a1b-2c3d4e5f6a7b',
                        state,
                        displayLanguageContext.displayLanguageSelected
                          .resources!,
                        'game'
                      ) || 'Point'
                }`}</span>
              )}
              <span className={'fw-bold'} style={{ fontSize: '20px' }}>
                {status === 'correct'
                  ? generateTitleById(
                      '5c9d8d3c-0e10-6f71-bc3d-4e5f6a7b8c9d',
                      state,
                      displayLanguageContext.displayLanguageSelected.resources!,
                      'game'
                    ) || 'Excellent!'
                  : status === 'partial'
                    ? generateTitleById(
                        '6a7b8c9d-0e1f-2a3b-4c5d-6e7f8a9b0c1d',
                        state,
                        displayLanguageContext.displayLanguageSelected
                          .resources!,
                        'game'
                      ) || 'Partially correct'
                    : generateTitleById(
                        '6d0e9e4d-1f11-7082-cd4e-5f6a7b8c9d0e',
                        state,
                        displayLanguageContext.displayLanguageSelected
                          .resources!,
                        'game'
                      ) || 'Unlucky.'}
              </span>
            </div>
            <div className={'d-flex justify-content-center mb-auto p-3'}>
              {message}
            </div>
          </div>
        </>
      )
    };
    const content: AchievementContent[] = toast.content;
    content.push(addItemNotification);
    setToast({ ...toast, content });
  };

  const generateARMiniGameLink = React.useMemo(() => {
    if (!selectedTaskContent?.ARMiniGame) return '';
    const ARGames = ArMiniGames[selectedTaskContent?.ARMiniGame];
    let timer = ARGames.defaultTimer;
    const baseUrl = getOtherBaseUrl();

    if (selectedTaskContent && selectedTaskContent.ARMiniGameSetting) {
      // implement timer from AR Game Setting
      timer =
        selectedTaskContent.ARMiniGameSetting.scoringReduction?.maximumTimeLimit?.toString() ??
        ARGames.defaultTimer;
    }

    let modeParam = '';
    if (selectedTaskContent.ARMiniGame === 'Where in the World?') {
      modeParam = '&mode=0';
    } else if (
      selectedTaskContent.ARMiniGame === 'Where in the World? - Easy' ||
      selectedTaskContent.ARMiniGame === 'Where in the World? - Hard'
    ) {
      const mode = selectedTaskContent.ARMiniGameSetting?.mode ?? 0;
      modeParam = `&mode=${mode}`;
    }

    return `${baseUrl}/mini-games/?type=${ARGames.type}&gameCode=${state.gameCode}&playerCode=${player.playerState?.code ?? ''}&taskId=${selectedTaskContent.id}&teamName=${player.playerState?.name}&timer=${timer}${modeParam}&env=${((window?._env_?.REACT_APP_API_BASE_URL || process.env.REACT_APP_API_BASE_URL) as string).replaceAll('https://', '')}`;
  }, [
    state.gameCode,
    selectedTaskContent?.id,
    player.playerState?.code,
    player.playerState?.name,
    selectedTaskContent?.ARMiniGame,
    selectedTaskContent?.ARMiniGameSetting
  ]);

  const openARApp = () => {
    if (deviceType === 'iOS') {
      handleCloseODT();
      window.location.href = generateARMiniGameLink;
    } else {
      setShowARWarning();
    }
  };

  React.useEffect(() => {
    (async () => {
      if (selectedTaskContent && state.gameDocument) {
        if (selectedTaskContent.contentResId) {
          const { intro, introBlobs, introContent } =
            isContainPremessageOrIntro(
              state.gameDocument,
              taskContentId,
              player?.playerState?.language?.name ?? ''
            );
          if (intro) {
            if (player.playerState?.isPreloadCompleted) {
              setHasIntro({
                content: introContent,
                embeddedBlobs: introBlobs
              });
            } else {
              const result = await processMediaTags(introContent);
              setHasIntro({ content: result, embeddedBlobs: [] });
            }
          } else if (formQuestions.length === 0) {
            if (selectedTaskContent.ARMiniGame) {
              openARApp();
            } else {
              // should be auto complete if no intro and forms
              updateTaskStatusToCompleted();
            }
          } else {
            setSelectedTab('Questions');
          }
        }
      }
    })();
  }, [
    state.gameDocument,
    selectedTaskContent,
    formQuestions.length,
    player?.playerState?.language?.name
  ]);

  React.useEffect(() => {
    setTotalTryAnswer(0);
    (async function fetchData() {
      if (currentFormIndex > -1) {
        if (
          formQuestions &&
          formQuestions[currentFormIndex]?.isScoringReduction
        ) {
          await getScoringReduction();
        }
        repopulateQuestion();
      }
    })();
  }, [currentFormIndex, formQuestions]);

  React.useEffect(() => {
    const playerCode = player.playerState?.code ?? '';
    if (!playerCode) return;

    (async () => {
      const uploads = await getFromStore<TFileUpload>(
        'upload-queue',
        playerCode
      );
      if (!uploads) return;
      if (uploads.files.length === 0) return;

      const taskId = taskContentId;
      const formId = currentFormId;
      const selectedUploads = uploads.files.find(
        (f) => f.taskId === taskId && f.formId === formId
      );
      if (selectedUploads)
        setContentAnswer([
          {
            value: '',
            size: 0,
            answerResId: ''
          }
        ]);
    })();
  }, [player.playerState?.code, taskContentId, currentFormId]);

  return (
    <>
      {hasIntro || formQuestions.length > 0 ? (
        <div className={'task-content__mask'} key={currentFormId}>
          <TimerProgressForm
            formIndex={currentFormIndex}
            taskContentId={taskContentId}
            onSubmitHandler={onSubmitHandler}
          />

          <Container className={'task-content px-0'} fluid>
            <div className={'content__header'}>
              <h1 className={'fw-bold p-0 m-0'}>
                {GetResourceValue(
                  state.gameDocument!,
                  selectedTaskContent?.titleResId ?? '',
                  player?.playerState?.language?.name
                )}
              </h1>
              <div
                className={`${isUnableToCloseOrChangeForm ? 'd-none' : ''} d-flex justify-content-center align-items-center cursor-pointer`}
                onClick={handleCloseODT}>
                <span className={'material-symbols-outlined fw-bold'}>
                  close
                </span>
              </div>
            </div>

            <div className={'d-flex gap-4 border-bottom px-3 pt-3'}>
              {[
                ...(hasIntro ? ['Task information'] : []),
                ...(formQuestions.length > 0 ? ['Questions'] : [])
              ].map((tab) => (
                <div
                  key={tab}
                  style={{
                    cursor: 'pointer',
                    ...(selectedTab === tab && {
                      color: '#003A64',
                      fontWeight: 'bold',
                      paddingBottom: '5px',
                      padding: '0 5px 8px',
                      borderBottom: '3px solid #003A64'
                    })
                  }}
                  onClick={() => setSelectedTab(tab)}>
                  {tab}
                </div>
              ))}
            </div>

            {selectedTab === 'Task information' && (
              <>
                <div className={'content__body'}>
                  {hasIntro?.content && (
                    <div className={'content__intro'}>
                      {selectedTaskContent?.bodyType === 'HTML' ? (
                        <RichtextContent
                          content={hasIntro.content ?? ''}
                          embeddedBlobs={hasIntro.embeddedBlobs}
                        />
                      ) : (
                        <ReactMarkdown rehypePlugins={[rehypeRaw]}>
                          {hasIntro.content ?? ''}
                        </ReactMarkdown>
                      )}
                    </div>
                  )}
                </div>

                <div className={'content__footer'}>
                  <div className={'content'}>
                    <div className="d-flex align-items-center justify-content-end">
                      <Button
                        themeColor={'success'}
                        onClick={() => {
                          if (selectedTaskContent?.ARMiniGame) {
                            openARApp();
                          } else if (formQuestions.length > 0) {
                            setSelectedTab('Questions');
                          } else {
                            updateTaskStatusToCompleted();
                          }
                        }}>
                        <span className={'d-flex align-items-center'}>
                          <span>
                            {generateTitleById(
                              'be998642-6834-40d7-b457-55acafbe0fc0',
                              state,
                              displayLanguageContext.displayLanguageSelected
                                .resources!,
                              'game'
                            ) || 'Continue'}
                          </span>
                        </span>
                      </Button>
                    </div>
                  </div>
                </div>
              </>
            )}

            {selectedTab === 'Questions' && (
              <>
                <div className={'content__body'}>
                  <div className={`d-flex justify-content-center`}>
                    <div
                      className={`d-flex flex-column align-items-center ${['text', 'number', 'time'].includes(currentAnswerType) ? 'short-answer' : 'long-answer'}`}>
                      {(formQuestions ?? []).length > 1 && (
                        <h2 className={'fw-bold'}>
                          {currentFormIndex + 1}/{(formQuestions ?? []).length}
                        </h2>
                      )}

                      <h2 className={'fw-bold'}>
                        {GetResourceValue(
                          state.gameDocument!,
                          formQuestions[currentFormIndex].questionResId ?? '',
                          player?.playerState?.language?.name ?? ''
                        )}
                      </h2>

                      {formMedia.length > 0 && (
                        <div className={'d-flex justify-content-center mb-1'}>
                          <Gallery
                            className={'task-content-media'}
                            items={formMedia}
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  <div
                    id={'task-content-answer'}
                    className={`d-flex flex-column flex-grow-1 ${
                      isDisabledAnswer() ? 'disabled-answer' : ''
                    }`}>
                    <Answer
                      key={currentFormIndex}
                      answerInput={
                        currentAnswerType === 'number'
                          ? (contentAnswer[0]?.value ?? 0)
                          : (contentAnswer[0]?.value ?? '')
                      }
                      answerOptionIndex={
                        contentAnswer?.findIndex(
                          (item: ContentAnswer) => item.value as boolean
                        ) ?? -1
                      }
                      answerType={currentAnswerType}
                      checkboxOption={checkboxOption}
                      formId={currentFormId}
                      isCorrectAnswer={getPlayerAnswerStatus()}
                      isManualScoring={
                        formQuestions && currentFormIndex > -1
                          ? formQuestions[currentFormIndex].isManualScoring
                          : false
                      }
                      name={selectedTaskContent?.name}
                      onChangeAnswerInput={onSingleInputChangeAnswer}
                      onChangeOptionIndex={onRadioChangeAnswer}
                      onCheckboxChange={onCheckboxChangeHandler}
                      options={options}
                      taskId={taskContentId}
                      isDisabled={isDisabledAnswer()}
                      isUploading={isUploading}
                      isOnline={isOnline}
                      uploadStatus={
                        uploadStatus[`${taskContentId}_${currentFormId}`]
                      }
                      uploadError={
                        uploadErrors[`${taskContentId}_${currentFormId}`]
                      }
                      onRetry={() => {
                        if (currentFormId)
                          retryUpload(taskContentId, currentFormId);
                      }}
                      onCancel={() => {
                        if (currentFormId)
                          cancelUpload(taskContentId, currentFormId);
                      }}
                    />
                  </div>
                </div>

                <div className={'content__footer'}>
                  <div className={'content'}>
                    <div
                      className={`align-items-center d-flex justify-content-${currentFormIndex > 0 ? 'between' : 'end'}`}>
                      {currentFormIndex > 0 && (
                        <Button
                          fillMode={'flat'}
                          disabled={isUploading}
                          className={`fw-bold ${isUnableToCloseOrChangeForm ? 'd-none' : ''}`}
                          onClick={() => handleNavigation('prev')}>
                          <span className={'d-flex align-items-center'}>
                            <span className={'material-symbols-outlined'}>
                              navigate_before
                            </span>
                            {generateTitleById(
                              '714c5007-ce05-477e-8dc2-8f9502eb4c45',
                              state,
                              displayLanguageContext.displayLanguageSelected
                                .resources!,
                              'game'
                            ) || 'Previous'}
                          </span>
                        </Button>
                      )}

                      <div className="d-flex justify-items-between">
                        {renderPlayerAttempts()}
                      </div>

                      <div className="d-flex align-items-center justify-content-end">
                        {formQuestions.length - 1 !== currentFormIndex && (
                          <Button
                            fillMode={'flat'}
                            disabled={isUploading}
                            className={`mr-2 fw-bold ${isUnableToCloseOrChangeForm ? 'd-none' : ''}`}
                            onClick={() => handleNavigation('next')}>
                            {generateTitleById(
                              '079c039a-8207-4404-8eb6-558cf5d8477b',
                              state,
                              displayLanguageContext.displayLanguageSelected
                                .resources!,
                              'game'
                            ) || 'Next'}
                          </Button>
                        )}

                        <Button
                          id={'btn-task-content-submit'}
                          themeColor={'success'}
                          onClick={() => {
                            const isTimerQuestion =
                              formQuestions[currentFormIndex]
                                .isScoringReduction;

                            // if not timer question but answer still empty, show warning
                            if (
                              !isTimerQuestion &&
                              contentAnswer.length === 0 &&
                              currentAnswerType !== 'none'
                            ) {
                              setShowNoAsnwerWarning(true);
                              return;
                            }

                            onSubmitHandler();
                          }}
                          disabled={isDisabledAnswer() || isUploading}>
                          {`${
                            generateTitleById(
                              '8065dbef-51c3-4e7b-82e8-48ea0bfdf093',
                              state,
                              displayLanguageContext.displayLanguageSelected
                                .resources!,
                              'game'
                            ) || 'Submit'
                          }`}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </Container>

          {showNoAsnwerWarning && (
            <ConfirmationDialog
              title=" "
              icon={'warning'}
              widthDialog={500}
              message={generatePopupMessage(currentAnswerType)}
              cancelText={
                generateTitleById(
                  '48bc820b-40e8-4624-8d46-b68e16013be6',
                  state,
                  displayLanguageContext.displayLanguageSelected.resources!,
                  'game'
                ) || 'Close'
              }
              onCancelCallback={() => setShowNoAsnwerWarning(false)}
            />
          )}
        </div>
      ) : null}

      {showARWarning && (
        <ConfirmationDialog
          title={'Unsupported Device'}
          icon={'warning'}
          widthDialog={500}
          message={
            'This task cannot be opened on your current device. Please use an iOS device to access this task.'
          }
          cancelText={
            generateTitleById(
              '48bc820b-40e8-4624-8d46-b68e16013be6',
              state,
              displayLanguageContext.displayLanguageSelected.resources!,
              'game'
            ) || 'Close'
          }
          onCancelCallback={() => {
            handleCloseODT();
            setShowARWarning();
          }}
        />
      )}
    </>
  );
};
